// File: frontend/src/data/filmRoomChapters.ts
//
// Film Room chapters: one curated player-performance per chapter, synced to an
// official LALIGA EA SPORTS full-match upload on YouTube via per-half kickoff
// anchors. Source of truth for moments: prod fantasy_score rows joined to
// match_event_raw_data on the StatsBomb event UUID; `periodSeconds` is the
// floor of the in-period event timestamp. videoSeconds = period anchor +
// periodSeconds, so the mapping is exact (including stoppage time) as long as
// the upload's halves are uncut. A null anchor means the kickoff time for that
// half hasn't been read off the video yet — the UI falls back gracefully.
//
// Regenerated 2026-07-29 (night) after the rule-6 rescore (progression window
// logs all pass attempts' origins; 25s lookback). Palmer lost his 43' corner
// re-delivery IP (55.0); Bruno lost a 62' IC and the 90+6' IP (37.0 — the SCA
// on that ball survives). All seven chapter totals verified against prod.
// If a chapter's match is ever recalculated again, re-pull its moments.

export interface FilmRoomScore {
  scoreType: string; // matches fantasy_score.score_type / STAT_ABBREVIATIONS keys
  points: number;
  // For SCA/GCA only: the creating action's event type (the credited event
  // itself), rendered as an explicit "why this counts" callout in the UI.
  via?: 'pass' | 'shot' | 'foul-won';
}

// A "moment" is every fantasy score sharing one StatsBomb event timestamp —
// one click, one clip, possibly several point lines (a goal is Goal + SOT + Shot).
export interface FilmRoomMoment {
  period: number;
  minute: number; // cumulative match-clock minute (feeds formatGametime)
  periodSeconds: number; // seconds into the half (StatsBomb in-period timestamp)
  // Curation escape hatch: hide a row the video doesn't support (feed noise)
  // WITHOUT touching sums — hidden moments still count toward chapter and
  // running totals, they just don't render.
  hidden?: boolean;
  scores: FilmRoomScore[];
}

export interface FilmRoomChapter {
  slug: string;
  videoId: string;
  anchors: { p1: number | null; p2: number | null }; // video seconds at each kickoff
  player: { nickname: string; teamName: string };
  match: {
    homeTeam: string;
    awayTeam: string;
    homeGoals: number;
    awayGoals: number;
    competition: string;
    matchDate: string;
  };
  blurb: string; // one-sentence chapter intro shown on the player card
  // Editorially chosen lobby preview. Indexes point into moments and render
  // chronologically; the helper below supplies a deduplicated fallback.
  previewMomentIndexes?: number[];
  moments: FilmRoomMoment[];
}

// How many seconds before the event the video seeks to, so the buildup reads.
// Carries get a longer lead: the feed stamps carry events at the carry END
// (verified against video), so 5s would only show the tail of the run.
export const SEEK_LEAD_SECONDS = 7; // owner-tuned 2026-07-29 (was 5: "a little tight")
export const CARRY_SEEK_LEAD_SECONDS = 10;

export const momentTotal = (m: FilmRoomMoment): number =>
  m.scores.reduce((sum, s) => sum + s.points, 0);

export const seekLeadSeconds = (m: FilmRoomMoment): number =>
  m.scores.some((s) => s.scoreType === 'ProgCarry')
    ? CARRY_SEEK_LEAD_SECONDS
    : SEEK_LEAD_SECONDS;

// null when the half's kickoff anchor hasn't been measured yet.
export const videoSecondsFor = (
  chapter: FilmRoomChapter,
  moment: FilmRoomMoment,
): number | null => {
  const anchor = moment.period === 1 ? chapter.anchors.p1 : chapter.anchors.p2;
  return anchor === null ? null : anchor + moment.periodSeconds;
};

export const chapterTotalPoints = (chapter: FilmRoomChapter): number =>
  chapter.moments.reduce((sum, m) => sum + momentTotal(m), 0);

// League grouping for the two-tier pill UI (league pills -> player pills).
export interface FilmRoomLeague {
  slug: string;
  label: string;
}
export const FILM_ROOM_LEAGUES: FilmRoomLeague[] = [
  { slug: 'laliga', label: 'La Liga' },
  { slug: 'epl', label: 'Premier League' },
];
export const DEFAULT_LEAGUE_SLUG = 'laliga';

export const chapterLeague = (chapter: FilmRoomChapter): string =>
  chapter.match.competition.startsWith('La Liga') ? 'laliga' : 'epl';

export const chaptersForLeague = (leagueSlug: string): FilmRoomChapter[] =>
  FILM_ROOM_CHAPTERS.filter((c) => chapterLeague(c) === leagueSlug);

const CREATION_TYPES = new Set(['GCA', 'SCA']);
const PROGRESSION_TYPES = new Set(['ProgPass', 'ProgCarry']);
const ATTACKING_TYPES = new Set(['Goal', 'ShotOnTarget', 'Shot']);

/** Three distinct, chronological teaching moments for the compact lobby card. */
export const previewMomentIndexesFor = (chapter: FilmRoomChapter): number[] => {
  const selected: number[] = [];
  const add = (index: number) => {
    if (
      Number.isInteger(index)
      && index >= 0
      && index < chapter.moments.length
      && !chapter.moments[index].hidden
      && !selected.includes(index)
    ) {
      selected.push(index);
    }
  };

  (chapter.previewMomentIndexes ?? []).forEach(add);

  const categoryPredicates = [CREATION_TYPES, PROGRESSION_TYPES, ATTACKING_TYPES];
  for (const scoreTypes of categoryPredicates) {
    if (selected.length >= 3) break;
    const candidate = chapter.moments
      .map((moment, index) => ({ moment, index }))
      .filter(({ moment, index }) =>
        !selected.includes(index) && moment.scores.some((score) => scoreTypes.has(score.scoreType)))
      .sort((a, b) => momentTotal(b.moment) - momentTotal(a.moment))[0];
    if (candidate) add(candidate.index);
  }

  chapter.moments
    .map((moment, index) => ({ moment, index }))
    .filter(({ index }) => !selected.includes(index))
    .sort((a, b) => momentTotal(b.moment) - momentTotal(a.moment))
    .forEach(({ index }) => {
      if (selected.length < 3) add(index);
    });

  return selected.slice(0, 3).sort((a, b) => a - b);
};

// ---------------------------------------------------------------------------
// Chapter 1: Lamine Yamal — Barcelona 4-1 Espanyol (2026-04-11, match_id 1385508)
// Video w51Fu3Ll-3U (official LALIGA upload): 1H kickoff 0:08, 2H 49:57
// (anchors read off the video by the owner, 2026-08-06). Swapped from the
// Real Sociedad 2-1 loss (yI2mFcBij9o): the launcher thumbnail there showed
// Oyarzabal, and a losing effort shouldn't front Yamal's chapter. This is the
// ceiling game instead — 50.0 points, a goal, three GCA, and a hand in every
// Barcelona goal of the derbi. Moments pulled from prod fantasy_score joined
// to match_event_raw_data 2026-08-06; total verified 50.0.
// ---------------------------------------------------------------------------
const YAMAL_CHAPTER: FilmRoomChapter = {
  slug: 'yamal',
  videoId: 'w51Fu3Ll-3U',
  anchors: { p1: 8, p2: 2997 },
  player: { nickname: 'Lamine Yamal', teamName: 'Barcelona' },
  match: {
    homeTeam: 'Barcelona',
    awayTeam: 'Espanyol',
    homeGoals: 4,
    awayGoals: 1,
    competition: 'La Liga 2025/26',
    matchDate: '2026-04-11',
  },
  blurb: 'Lamine Yamal drops 50 in the derbi - a goal, three goal-creating actions, and a hand in every Barcelona goal.',
  previewMomentIndexes: [3, 9, 14],
  moments: [
    // ---- First half ----
    { period: 1, minute: 5, periodSeconds: 313, scores: [{ scoreType: 'Dispossessed', points: -1 }] },
    {
      // Give-and-go: his pass makes the chance, the return makes his shot —
      // one clip, both lines (merged 2026-08-06, same-clip curation pass).
      period: 1, minute: 7, periodSeconds: 430,
      scores: [{ scoreType: 'SCA', points: 1.5, via: 'pass' }, { scoreType: 'Shot', points: 1 }],
    },
    { period: 1, minute: 8, periodSeconds: 512, scores: [{ scoreType: 'ProgCarry', points: 1 }] },
    {
      // Corner delivery headed home — the first goal-creating action.
      period: 1, minute: 8, periodSeconds: 539,
      scores: [{ scoreType: 'GCA', points: 6, via: 'pass' }],
    },
    {
      // Carry, feed, goal — one sequence worth three lines.
      period: 1, minute: 24, periodSeconds: 1463,
      scores: [
        { scoreType: 'GCA', points: 6, via: 'pass' },
        { scoreType: 'ProgCarry', points: 1 },
        { scoreType: 'ProgPass', points: 1 },
      ],
    },
    { period: 1, minute: 30, periodSeconds: 1829, scores: [{ scoreType: 'Shot', points: 1 }] },
    {
      period: 1, minute: 46, periodSeconds: 2761,
      scores: [{ scoreType: 'ProgCarry', points: 1 }, { scoreType: 'Shot', points: 1 }],
    },
    { period: 1, minute: 46, periodSeconds: 2791, scores: [{ scoreType: 'SCA', points: 1.5, via: 'pass' }] },
    // ---- Second half ----
    {
      period: 2, minute: 47, periodSeconds: 126,
      scores: [{ scoreType: 'ProgCarry', points: 1 }, { scoreType: 'Shot', points: 1 }],
    },
    {
      period: 2, minute: 58, periodSeconds: 812,
      scores: [{ scoreType: 'ShotOnTarget', points: 3 }, { scoreType: 'Shot', points: 1 }],
    },
    { period: 2, minute: 59, periodSeconds: 880, scores: [{ scoreType: 'ProgCarry', points: 1 }] },
    { period: 2, minute: 62, periodSeconds: 1039, scores: [{ scoreType: 'ProgPass', points: 1 }] },
    { period: 2, minute: 62, periodSeconds: 1055, scores: [{ scoreType: 'Dispossessed', points: -1 }] },
    {
      period: 2, minute: 84, periodSeconds: 2359,
      scores: [{ scoreType: 'ProgCarry', points: 1 }, { scoreType: 'Shot', points: 1 }],
    },
    {
      // The goal.
      period: 2, minute: 86, periodSeconds: 2484,
      scores: [
        { scoreType: 'Goal', points: 8 },
        { scoreType: 'ShotOnTarget', points: 3 },
        { scoreType: 'Shot', points: 1 },
      ],
    },
    {
      // Carries, releases, assist — Barcelona's fourth.
      period: 2, minute: 88, periodSeconds: 2588,
      scores: [
        { scoreType: 'GCA', points: 6, via: 'pass' },
        { scoreType: 'ProgCarry', points: 1 },
        { scoreType: 'ProgPass', points: 1 },
      ],
    },
  ],
};

// ---------------------------------------------------------------------------
// Chapter 2: Vinícius Júnior — Real Madrid 3-2 Atlético Madrid (match_id 1385474)
// Video FXDhUEXMDrs (official LALIGA upload): 1H kickoff at 0:06, 2H at 47:28.
// ---------------------------------------------------------------------------
const VINI_CHAPTER: FilmRoomChapter = {
  slug: 'vini',
  videoId: 'FXDhUEXMDrs',
  anchors: { p1: 6, p2: 2848 },
  player: { nickname: 'Vinícius Júnior', teamName: 'Real Madrid' },
  match: {
    homeTeam: 'Real Madrid',
    awayTeam: 'Atlético Madrid',
    homeGoals: 3,
    awayGoals: 2,
    competition: 'La Liga 2025/26',
    matchDate: '2026-03-22',
  },
  blurb: 'Vini Jr scores twice in a hard fought win over Atletico.',
  previewMomentIndexes: [5, 8, 11],
  moments: [
    // ---- First half ----
    { period: 1, minute: 1, periodSeconds: 68, scores: [{ scoreType: 'ProgPass', points: 1 }] },
    { period: 1, minute: 3, periodSeconds: 197, scores: [{ scoreType: 'ProgPass', points: 1 }] },
    { period: 1, minute: 4, periodSeconds: 283, scores: [{ scoreType: 'Dispossessed', points: -1 }] },
    { period: 1, minute: 16, periodSeconds: 1018, scores: [{ scoreType: 'Dispossessed', points: -1 }] },
    { period: 1, minute: 19, periodSeconds: 1163, scores: [{ scoreType: 'ProgCarry', points: 1 }] },
    {
      // Saved shot, rebounded himself 0.9s later, saved again — one clip,
      // both shots' lines (merged 2026-08-06, same-clip curation pass). The
      // self-SCA this used to carry died under the 2026-07-30 no-self-assist
      // rule — this is the very case that motivated it.
      period: 1, minute: 21, periodSeconds: 1288,
      scores: [
        { scoreType: 'ShotOnTarget', points: 3 },
        { scoreType: 'Shot', points: 1 },
        { scoreType: 'ShotOnTarget', points: 3 },
        { scoreType: 'Shot', points: 1 },
      ],
    },
    { period: 1, minute: 23, periodSeconds: 1384, scores: [{ scoreType: 'Shot', points: 1 }] },
    { period: 1, minute: 28, periodSeconds: 1692, scores: [{ scoreType: 'SCA', points: 1.5, via: 'pass' }] },
    { period: 1, minute: 38, periodSeconds: 2287, scores: [{ scoreType: 'ProgCarry', points: 1 }] },
    // ---- Second half ----
    {
      period: 2, minute: 51, periodSeconds: 396,
      scores: [
        { scoreType: 'Goal', points: 8 },
        { scoreType: 'ShotOnTarget', points: 3 },
        { scoreType: 'Shot', points: 1 },
      ],
    },
    { period: 2, minute: 60, periodSeconds: 930, scores: [{ scoreType: 'ProgCarry', points: 1 }] },
    {
      period: 2, minute: 71, periodSeconds: 1583,
      scores: [
        { scoreType: 'Goal', points: 8 },
        { scoreType: 'ShotOnTarget', points: 3 },
        { scoreType: 'Shot', points: 1 },
      ],
    },
    { period: 2, minute: 82, periodSeconds: 2228, scores: [{ scoreType: 'SCA', points: 1.5, via: 'foul-won' }] },
  ],
};

// ---------------------------------------------------------------------------
// Chapters 3 + 4: Mbappé and Bellingham — Real Madrid 2-1 Barcelona, the
// October Clásico (match_id from 2025-10-26). One video, two protagonists:
// the same match watched through two players' scoring logs. Their moments
// interlock — Bellingham's 21' GCA is the pass for Mbappé's goal three seconds
// later, and Mbappé's 45+1' SCA creates Bellingham's stoppage-time shot.
// Video TSIV7SW8nq8 (official LALIGA upload): 1H kickoff 7:28, 2H 1:02:00.
// ---------------------------------------------------------------------------
const MBAPPE_CHAPTER: FilmRoomChapter = {
  slug: 'mbappe',
  videoId: 'TSIV7SW8nq8',
  anchors: { p1: 448, p2: 3720 },
  player: { nickname: 'Kylian Mbappé', teamName: 'Real Madrid' },
  match: {
    homeTeam: 'Real Madrid',
    awayTeam: 'Barcelona',
    homeGoals: 2,
    awayGoals: 1,
    competition: 'La Liga 2025/26',
    matchDate: '2025-10-26',
  },
  blurb: 'Mbappe with an attacking & creating masterclass in El Clasico.',
  previewMomentIndexes: [0, 5, 6],
  moments: [
    // ---- First half ----
    {
      period: 1, minute: 21, periodSeconds: 1267,
      scores: [
        { scoreType: 'Goal', points: 8 },
        { scoreType: 'ShotOnTarget', points: 3 },
        { scoreType: 'Shot', points: 1 },
      ],
    },
    { period: 1, minute: 25, periodSeconds: 1515, scores: [{ scoreType: 'SCA', points: 1.5, via: 'pass' }] },
    {
      period: 1, minute: 28, periodSeconds: 1709,
      scores: [{ scoreType: 'ShotOnTarget', points: 3 }, { scoreType: 'Shot', points: 1 }],
    },
    {
      period: 1, minute: 34, periodSeconds: 2043,
      scores: [{ scoreType: 'SCA', points: 1.5, via: 'pass' }, { scoreType: 'ProgCarry', points: 1 }],
    },
    { period: 1, minute: 45, periodSeconds: 2702, scores: [{ scoreType: 'SCA', points: 1.5, via: 'pass' }] },
    // ---- Second half ----
    { period: 2, minute: 46, periodSeconds: 98, scores: [{ scoreType: 'ProgPass', points: 1 }] },
    {
      period: 2, minute: 51, periodSeconds: 386,
      scores: [
        { scoreType: 'ShotOnTarget', points: 3 },
        { scoreType: 'Shot', points: 1 },
        { scoreType: 'SCA', points: 1.5, via: 'shot' },
      ],
    },
    { period: 2, minute: 59, periodSeconds: 887, scores: [{ scoreType: 'Dispossessed', points: -1 }] },
    { period: 2, minute: 65, periodSeconds: 1254, scores: [{ scoreType: 'SCA', points: 1.5, via: 'pass' }] },
    {
      // Pass, carry, shot, chance created — one 9-second passage, one clip
      // (merged 2026-08-06, same-clip curation pass).
      period: 2, minute: 79, periodSeconds: 2061,
      scores: [
        { scoreType: 'SCA', points: 1.5, via: 'pass' },
        { scoreType: 'SCA', points: 1.5, via: 'shot' },
        { scoreType: 'Shot', points: 1 },
        { scoreType: 'ProgCarry', points: 1 },
      ],
    },
    {
      period: 2, minute: 87, periodSeconds: 2559,
      scores: [{ scoreType: 'Shot', points: 1 }, { scoreType: 'ProgCarry', points: 1 }],
    },
  ],
};

const BELLINGHAM_CHAPTER: FilmRoomChapter = {
  slug: 'bellingham',
  videoId: 'TSIV7SW8nq8',
  anchors: { p1: 448, p2: 3720 },
  player: { nickname: 'Jude Bellingham', teamName: 'Real Madrid' },
  match: {
    homeTeam: 'Real Madrid',
    awayTeam: 'Barcelona',
    homeGoals: 2,
    awayGoals: 1,
    competition: 'La Liga 2025/26',
    matchDate: '2025-10-26',
  },
  blurb: 'Bellingham powers Madrid to the win in El Clasico.',
  previewMomentIndexes: [0, 2, 6],
  moments: [
    // ---- First half ----
    {
      // The pass that becomes Mbappé's goal three seconds later.
      period: 1, minute: 21, periodSeconds: 1264,
      scores: [{ scoreType: 'GCA', points: 6, via: 'pass' }, { scoreType: 'ProgPass', points: 1 }],
    },
    { period: 1, minute: 24, periodSeconds: 1474, scores: [{ scoreType: 'Shot', points: 1 }] },
    {
      period: 1, minute: 28, periodSeconds: 1707,
      scores: [{ scoreType: 'SCA', points: 1.5, via: 'pass' }, { scoreType: 'ProgPass', points: 1 }],
    },
    { period: 1, minute: 29, periodSeconds: 1794, scores: [{ scoreType: 'Dispossessed', points: -1 }] },
    { period: 1, minute: 33, periodSeconds: 2039, scores: [{ scoreType: 'Dispossessed', points: -1 }] },
    {
      period: 1, minute: 36, periodSeconds: 2193,
      scores: [{ scoreType: 'ShotOnTarget', points: 3 }, { scoreType: 'Shot', points: 1 }],
    },
    {
      period: 1, minute: 42, periodSeconds: 2524,
      scores: [
        { scoreType: 'Goal', points: 8 },
        { scoreType: 'ShotOnTarget', points: 3 },
        { scoreType: 'Shot', points: 1 },
      ],
    },
    {
      period: 1, minute: 45, periodSeconds: 2702,
      scores: [{ scoreType: 'ShotOnTarget', points: 3 }, { scoreType: 'Shot', points: 1 }],
    },
    { period: 1, minute: 50, periodSeconds: 3053, scores: [{ scoreType: 'Dispossessed', points: -1 }] },
    // ---- Second half ----
    {
      period: 2, minute: 48, periodSeconds: 184,
      scores: [{ scoreType: 'SCA', points: 1.5, via: 'pass' }, { scoreType: 'ProgCarry', points: 1 }],
    },
    { period: 2, minute: 65, periodSeconds: 1252, scores: [{ scoreType: 'SCA', points: 1.5, via: 'pass' }] },
    {
      period: 2, minute: 66, periodSeconds: 1284,
      scores: [{ scoreType: 'SCA', points: 1.5, via: 'shot' }, { scoreType: 'Shot', points: 1 }],
    },
    { period: 2, minute: 72, periodSeconds: 1655, scores: [{ scoreType: 'Dispossessed', points: -1 }] },
  ],
};


// ---------------------------------------------------------------------------
// Chapter 5: Cole Palmer — Tottenham 3-4 Chelsea (2024-12-08). First EPL
// chapter, via NBC Sports' official "Premier League Classics" full-match
// upload. Video vTmz85EvYCQ: 1H kickoff 2:26, 2H 50:38. His log covers every
// creation pathway: via pass, via shot (rebound assist), via foul won.
// ---------------------------------------------------------------------------
const PALMER_CHAPTER: FilmRoomChapter = {
  slug: 'palmer',
  videoId: 'vTmz85EvYCQ',
  anchors: { p1: 146, p2: 3038 },
  player: { nickname: 'Cole Palmer', teamName: 'Chelsea' },
  match: {
    homeTeam: 'Tottenham',
    awayTeam: 'Chelsea',
    homeGoals: 3,
    awayGoals: 4,
    competition: 'EPL 2024/25',
    matchDate: '2024-12-08',
  },
  blurb: 'Cole Palmer takes over a seven goal thriller in London.',
  previewMomentIndexes: [2, 15, 16],
  moments: [
    // ---- First half ----
    { period: 1, minute: 3, periodSeconds: 198, scores: [{ scoreType: 'SCA', points: 1.5, via: 'pass' }] },
    { period: 1, minute: 27, periodSeconds: 1653, scores: [{ scoreType: 'ProgPass', points: 1 }] },
    {
      period: 1, minute: 30, periodSeconds: 1858,
      scores: [
        { scoreType: 'ShotOnTarget', points: 3 },
        { scoreType: 'Shot', points: 1 },
        { scoreType: 'SCA', points: 1.5, via: 'shot' },
        { scoreType: 'ProgCarry', points: 1 },
      ],
    },
    { period: 1, minute: 35, periodSeconds: 2100, scores: [{ scoreType: 'ProgPass', points: 1 }] },
    { period: 1, minute: 35, periodSeconds: 2131, scores: [{ scoreType: 'Dispossessed', points: -1 }] },
    { period: 1, minute: 37, periodSeconds: 2255, scores: [{ scoreType: 'Dispossessed', points: -1 }] },
    {
      period: 1, minute: 40, periodSeconds: 2433,
      scores: [{ scoreType: 'SCA', points: 1.5, via: 'pass' }, { scoreType: 'ProgPass', points: 1 }],
    },
    { period: 1, minute: 43, periodSeconds: 2593, scores: [{ scoreType: 'ProgPass', points: 1 }] },
    // ---- Second half ----
    { period: 2, minute: 48, periodSeconds: 223, scores: [{ scoreType: 'SCA', points: 1.5, via: 'pass' }] },
    { period: 2, minute: 56, periodSeconds: 661, scores: [{ scoreType: 'SCA', points: 1.5, via: 'pass' }] },
    {
      period: 2, minute: 60, periodSeconds: 923,
      scores: [
        { scoreType: 'Goal', points: 8 },
        { scoreType: 'ShotOnTarget', points: 3 },
        { scoreType: 'Shot', points: 1 },
      ],
    },
    { period: 2, minute: 62, periodSeconds: 1041, scores: [{ scoreType: 'ProgPass', points: 1 }] },
    {
      // His shot forced the chance a teammate buried — rebound assist.
      period: 2, minute: 72, periodSeconds: 1669,
      scores: [{ scoreType: 'GCA', points: 6, via: 'shot' }, { scoreType: 'Shot', points: 1 }],
    },
    {
      period: 2, minute: 76, periodSeconds: 1876,
      scores: [{ scoreType: 'SCA', points: 1.5, via: 'pass' }, { scoreType: 'ProgPass', points: 1 }],
    },
    {
      // He won the foul that produced the goal — the foul-won creation path.
      period: 2, minute: 82, periodSeconds: 2222,
      scores: [{ scoreType: 'GCA', points: 6, via: 'foul-won' }],
    },
    {
      period: 2, minute: 83, periodSeconds: 2293,
      scores: [
        { scoreType: 'Goal', points: 8 },
        { scoreType: 'ShotOnTarget', points: 3 },
        { scoreType: 'Shot', points: 1 },
      ],
    },
  ],
};

// ---------------------------------------------------------------------------
// Chapters 6 + 7: Bruno Fernandes and Mohamed Salah — Liverpool 2-2
// Manchester United (2025-01-05). One NBC full-match video, two protagonists:
// the pure creator (39.0 without scoring, zero giveaways) and the star whose
// elite line pays the turnover tax (five Dispossessed).
// Video QK_9C81hkxk: 1H kickoff 1:49, 2H 49:34.
// ---------------------------------------------------------------------------
const BRUNO_CHAPTER: FilmRoomChapter = {
  slug: 'bruno',
  videoId: 'QK_9C81hkxk',
  anchors: { p1: 109, p2: 2974 },
  player: { nickname: 'Bruno Fernandes', teamName: 'Manchester United' },
  match: {
    homeTeam: 'Liverpool',
    awayTeam: 'Manchester United',
    homeGoals: 2,
    awayGoals: 2,
    competition: 'EPL 2024/25',
    matchDate: '2025-01-05',
  },
  blurb: 'Bruno Fernandes runs the show at Anfield without scoring.',
  previewMomentIndexes: [9, 10, 12],
  moments: [
    // ---- First half ----
    { period: 1, minute: 3, periodSeconds: 193, scores: [{ scoreType: 'ProgPass', points: 1 }] },
    { period: 1, minute: 17, periodSeconds: 1027, scores: [{ scoreType: 'ProgPass', points: 1 }] },
    {
      // Progressive ball then the key pass 11s later — one passage, one clip
      // (merged 2026-08-06, same-clip curation pass).
      period: 1, minute: 19, periodSeconds: 1163,
      scores: [{ scoreType: 'ProgPass', points: 1 }, { scoreType: 'SCA', points: 1.5, via: 'pass' }],
    },
    { period: 1, minute: 21, periodSeconds: 1313, scores: [{ scoreType: 'ProgPass', points: 1 }] },
    { period: 1, minute: 23, periodSeconds: 1399, scores: [{ scoreType: 'ProgCarry', points: 1 }] },
    { period: 1, minute: 24, periodSeconds: 1447, scores: [{ scoreType: 'SCA', points: 1.5, via: 'pass' }] },
    { period: 1, minute: 35, periodSeconds: 2117, scores: [{ scoreType: 'ProgCarry', points: 1 }] },
    {
      period: 1, minute: 41, periodSeconds: 2512,
      scores: [{ scoreType: 'SCA', points: 1.5, via: 'shot' }, { scoreType: 'Shot', points: 1 }],
    },
    { period: 1, minute: 43, periodSeconds: 2620, scores: [{ scoreType: 'ProgCarry', points: 1 }] },
    // ---- Second half ----
    {
      // The assist for United's equalizer.
      period: 2, minute: 51, periodSeconds: 382,
      scores: [{ scoreType: 'GCA', points: 6, via: 'pass' }],
    },
    {
      // The assist for the second — plus the pass qualified as progression.
      period: 2, minute: 79, periodSeconds: 2048,
      scores: [{ scoreType: 'GCA', points: 6, via: 'pass' }, { scoreType: 'ProgPass', points: 1 }],
    },
    { period: 2, minute: 83, periodSeconds: 2297, scores: [{ scoreType: 'ProgPass', points: 1 }] },
    {
      // One-two and shot on target 3s later — one clip, both lines
      // (merged 2026-08-06, same-clip curation pass).
      period: 2, minute: 88, periodSeconds: 2605,
      scores: [
        { scoreType: 'SCA', points: 1.5, via: 'pass' },
        { scoreType: 'ShotOnTarget', points: 3 },
        { scoreType: 'Shot', points: 1 },
      ],
    },
    { period: 2, minute: 88, periodSeconds: 2635, scores: [{ scoreType: 'SCA', points: 1.5, via: 'pass' }] },
    { period: 2, minute: 90, periodSeconds: 2735, scores: [{ scoreType: 'ProgPass', points: 1 }] },
    { period: 2, minute: 95, periodSeconds: 3022, scores: [{ scoreType: 'ProgPass', points: 1 }] },
    {
      // The ProgPass this ball once carried died under rule 6 (his own deep
      // free kick 12s earlier marks the territory) — the SCA survives:
      // progression pays novel territory, creation pays chances.
      period: 2, minute: 96, periodSeconds: 3087,
      scores: [{ scoreType: 'SCA', points: 1.5, via: 'pass' }],
    },
  ],
};

const SALAH_CHAPTER: FilmRoomChapter = {
  slug: 'salah',
  videoId: 'QK_9C81hkxk',
  anchors: { p1: 109, p2: 2974 },
  player: { nickname: 'Mohamed Salah', teamName: 'Liverpool' },
  match: {
    homeTeam: 'Liverpool',
    awayTeam: 'Manchester United',
    homeGoals: 2,
    awayGoals: 2,
    competition: 'EPL 2024/25',
    matchDate: '2025-01-05',
  },
  blurb: 'Salah scores and creates but pays the turnover tax at Anfield.',
  previewMomentIndexes: [3, 10, 13],
  moments: [
    // ---- First half ----
    { period: 1, minute: 6, periodSeconds: 364, scores: [{ scoreType: 'Dispossessed', points: -1 }] },
    { period: 1, minute: 12, periodSeconds: 742, scores: [{ scoreType: 'SCA', points: 1.5, via: 'pass' }] },
    { period: 1, minute: 14, periodSeconds: 859, scores: [{ scoreType: 'Dispossessed', points: -1 }] },
    {
      period: 1, minute: 15, periodSeconds: 918,
      scores: [{ scoreType: 'SCA', points: 1.5, via: 'pass' }, { scoreType: 'ProgPass', points: 1 }],
    },
    { period: 1, minute: 17, periodSeconds: 1038, scores: [{ scoreType: 'Dispossessed', points: -1 }] },
    { period: 1, minute: 31, periodSeconds: 1889, scores: [{ scoreType: 'Dispossessed', points: -1 }] },
    { period: 1, minute: 33, periodSeconds: 2027, scores: [{ scoreType: 'ProgCarry', points: 1 }] },
    {
      period: 1, minute: 37, periodSeconds: 2272,
      scores: [{ scoreType: 'SCA', points: 1.5, via: 'pass' }, { scoreType: 'ProgCarry', points: 1 }],
    },
    {
      period: 1, minute: 42, periodSeconds: 2535,
      scores: [{ scoreType: 'Shot', points: 1 }, { scoreType: 'ProgCarry', points: 1 }],
    },
    { period: 1, minute: 45, periodSeconds: 2716, scores: [{ scoreType: 'SCA', points: 1.5, via: 'pass' }] },
    // ---- Second half ----
    {
      period: 2, minute: 69, periodSeconds: 1450,
      scores: [
        { scoreType: 'Goal', points: 8 },
        { scoreType: 'ShotOnTarget', points: 3 },
        { scoreType: 'Shot', points: 1 },
      ],
    },
    {
      period: 2, minute: 70, periodSeconds: 1520,
      scores: [{ scoreType: 'ProgCarry', points: 1 }, { scoreType: 'Dispossessed', points: -1 }],
    },
    { period: 2, minute: 70, periodSeconds: 1536, scores: [{ scoreType: 'ProgPass', points: 1 }] },
    {
      // Drew the foul that produced the next chance.
      period: 2, minute: 72, periodSeconds: 1647,
      scores: [{ scoreType: 'SCA', points: 1.5, via: 'foul-won' }],
    },
    { period: 2, minute: 74, periodSeconds: 1766, scores: [{ scoreType: 'Shot', points: 1 }] },
    { period: 2, minute: 77, periodSeconds: 1977, scores: [{ scoreType: 'ProgCarry', points: 1 }] },
    { period: 2, minute: 84, periodSeconds: 2345, scores: [{ scoreType: 'ProgCarry', points: 1 }] },
    { period: 2, minute: 89, periodSeconds: 2652, scores: [{ scoreType: 'SCA', points: 1.5, via: 'pass' }] },
    {
      period: 2, minute: 93, periodSeconds: 2900,
      scores: [{ scoreType: 'SCA', points: 1.5, via: 'shot' }, { scoreType: 'Shot', points: 1 }],
    },
  ],
};

export const FILM_ROOM_CHAPTERS: FilmRoomChapter[] = [
  YAMAL_CHAPTER,
  VINI_CHAPTER,
  MBAPPE_CHAPTER,
  BELLINGHAM_CHAPTER,
  PALMER_CHAPTER,
  BRUNO_CHAPTER,
  SALAH_CHAPTER,
];
export const DEFAULT_CHAPTER_SLUG = 'yamal';

export const getChapter = (slug: string | undefined): FilmRoomChapter =>
  FILM_ROOM_CHAPTERS.find((c) => c.slug === slug) ||
  FILM_ROOM_CHAPTERS.find((c) => c.slug === DEFAULT_CHAPTER_SLUG)!;
